/**
 * CmsPagePublic — rendu public d'une page CMS via son path (catch-all React).
 * Le rendu des blocs est délégué à components/cms-blocks (BlockRenderer).
 */
import React, { useEffect, useState, Suspense, lazy } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../../lib/api";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";
import NotFoundPage from "./NotFoundPage";
import BlockRenderer from "../../components/cms-blocks/BlockRenderer";
import { useVariables, WEDDING_PILLAR, trackMariage } from "../../components/cms-blocks/shared";
import { useI18n } from "../../lib/i18n";

// Chargés à la demande : hors du bundle initial (perf premier chargement)
const AmenitizAvailability = lazy(() => import("../../components/AmenitizAvailability"));
const AccommodationFichePage = lazy(() => import("./AccommodationFichePageWrapper"));
const HorseFichePage = lazy(() => import("./HorseFichePage"));
const VenueFichePage = lazy(() => import("./VenueFichePage"));
const BuildingRoomPage = lazy(() => import("./BuildingRoomPage"));

/**
 * Détecte les patterns d'URL qui doivent afficher une fiche entité (fallback)
 * si aucune page CMS n'existe à ce path.
 */
function matchEntityRoute(pathname) {
  // Chambres du Manoir : /hebergements/le-manoir/{roomSlug}
  const manoir = pathname.match(/^\/hebergements\/le-manoir\/([^/]+)$/);
  if (manoir) return { type: "building_room", pagePath: pathname };
  // Chambres de la Maison du Lac : /hebergements/maison-du-lac/{roomSlug}
  const mdl = pathname.match(/^\/hebergements\/maison-du-lac\/([^/]+)$/);
  if (mdl) return { type: "building_room", pagePath: pathname };
  const acc = pathname.match(/^\/hebergements\/([^/]+)$/);
  if (acc) return { type: "accommodation", slug: acc[1] };
  const horse = pathname.match(/^\/haras\/([^/]+)$/);
  if (horse) return { type: "horse", slug: horse[1] };
  // Fiches contextuelles : /(mariages|seminaires|evenements-prives)/(les-espaces|les-hebergements|hebergements)/<slug>
  const ctx = pathname.match(/^\/(mariages|seminaires|evenements-prives)\/(les-espaces|les-hebergements|hebergements)\/([^/]+)$/);
  if (ctx) {
    const [, , section, slug] = ctx;
    if (section === "les-espaces") return { type: "venue", slug };
    return { type: "accommodation", slug };
  }
  // Haras/installations/<slug> → venue
  const haras = pathname.match(/^\/haras\/installations\/([^/]+)$/);
  if (haras) return { type: "venue", slug: haras[1] };
  return null;
}

/** Effets de la page pilier mariage : view + délégation de clics + CTA mobile sticky. */
function MariagePillarExtras() {
  React.useEffect(() => {
    trackMariage("view_mariage_page");
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const txt = (a.textContent || "").toLowerCase();
      if (href.startsWith("tel:")) trackMariage("click_phone_mariage");
      else if (href.startsWith("mailto:")) trackMariage("click_email_mariage");
      else if (href.includes("matterport")) trackMariage("click_360_rigoletto");
      else if (href.startsWith("/hebergements")) trackMariage("click_hebergements_mariage");
      else if (href.includes("/nous-contacter")) {
        const evt = txt.includes("visite") ? "click_organiser_visite" : "click_demande_devis";
        trackMariage(evt);
        try { sessionStorage.setItem("briouse:mariage_intent", "1"); } catch { /* noop */ }
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  return (
    <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white/95 backdrop-blur-sm border-t border-pierre px-4 py-2.5"
         data-testid="mariage-sticky-cta">
      <Link to="/nous-contacter" onClick={() => trackMariage("click_organiser_visite")}
            className="block w-full text-center bg-domaine text-lin py-3 text-xs uppercase tracking-[0.2em]"
            data-testid="mariage-sticky-cta-btn">
        Organiser une visite
      </Link>
    </div>
  );
}

const BREADCRUMB_SECTIONS = {
  mariages: { label: "Mariage", root: WEDDING_PILLAR },
  "le-domaine": { label: "Le Domaine", root: "/le-domaine" },
  haras: { label: "Le Haras", root: "/haras" },
  seminaires: { label: "Séminaires", root: "/seminaires" },
  "evenements-prives": { label: "Événements privés", root: "/evenements-prives" },
  tourisme: { label: "Tourisme", root: "/tourisme" },
  hebergements: { label: "Hébergements", root: "/hebergements" },
};

// Référentiel officiel (juin 2026) — pages piliers « lieu événementiel »
const EVENT_VENUE_PATHS = [
  "/mariages/le-domaine-pour-votre-mariage",
  "/seminaires/le-domaine-pour-votre-seminaire",
  "/evenements-prives/le-domaine-pour-votre-evenement",
];

// Référentiel officiel — hébergements (19 chambres physiques au total)
const LODGING_LD = {
  "/hebergements/le-manoir": { type: "BedAndBreakfast", name: "Le Manoir — Domaine de la Briouse", rooms: 8 },
  "/hebergements/maison-du-lac": { type: "BedAndBreakfast", name: "La Maison du Lac — Domaine de la Briouse", rooms: 7 },
  "/hebergements/gite-pavillon": { type: "VacationRental", name: "Le Gîte Pavillon — Domaine de la Briouse", rooms: 3 },
  "/hebergements/gite-carmen": { type: "VacationRental", name: "Le Gîte Carmen — Domaine de la Briouse", rooms: 1 },
};

function SectionBreadcrumb({ page, hasHero }) {
  const seg = page.path.split("/")[1];
  const section = BREADCRUMB_SECTIONS[seg];
  if (!section) return null;
  const isRoot = page.path === section.root;
  const items = [
    { label: "Accueil", href: "/" },
    { label: section.label, href: isRoot ? null : section.root },
    ...(isRoot ? [] : [{ label: page.title, href: null }]),
  ];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.label,
      item: `https://domainedelabriouse.com${it.href ?? page.path}`,
    })),
  };
  return (
    <nav aria-label="Fil d'ariane"
         className={`text-xs text-ardoise/60 ${hasHero ? "max-w-5xl mx-auto px-6 pt-4" : "mb-6"}`}
         data-testid="wedding-breadcrumb">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-or">›</span>}
            {it.href ? (
              <Link to={it.href} className="hover:text-domaine underline-offset-2 hover:underline"
                    data-testid={`breadcrumb-link-${i}`}>{it.label}</Link>
            ) : (
              <span className="text-ardoise" aria-current="page">{it.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function CmsPagePublic() {
  const { pathname } = useLocation();
  const [state, setState] = useState({ status: "loading", page: null });
  const [unit, setUnit] = useState(null);
  const vars = useVariables();
  const { locale } = useI18n();

  // Charge la fiche accommodation_unit correspondant au path (source de vérité)
  // — uniquement pour les paths /hebergements/* (évite un appel inutile sur les autres pages)
  useEffect(() => {
    setUnit(null);
    if (!pathname.startsWith("/hebergements/")) return;
    api.get(`/public/accommodation-units/by-path?path=${encodeURIComponent(pathname)}`)
      .then((r) => setUnit(r.data))
      .catch(() => setUnit(null));
  }, [pathname]);

  useEffect(() => {
    if (unit?.amenitiz_room_id && window.location.hash === "#reserver") {
      setTimeout(() => document.getElementById("reserver")?.scrollIntoView({ behavior: "smooth" }), 500);
    }
  }, [unit]);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", page: null });

    // Priorité 1 : les chambres du Manoir sont rendues par la fiche premium
    // (accommodation_units) et outrepassent toute page CMS legacy au même path.
    const entityFirst = matchEntityRoute(pathname);
    if (entityFirst?.type === "building_room") {
      setState({ status: "entity", entity: entityFirst, page: null });
      return () => { cancelled = true; };
    }

    // Lance en parallèle les 2 appels (redirect + page) pour éviter un aller-retour séquentiel
    const redirectP = api.get(`/public/cms/redirect?path=${encodeURIComponent(pathname)}`)
      .then((r) => r.data).catch(() => null);
    const pageP = api.get(`/public/cms/page?path=${encodeURIComponent(pathname)}&locale=${encodeURIComponent(locale)}`)
      .then((r) => r.data).catch(() => null);

    Promise.all([redirectP, pageP]).then(([redir, page]) => {
      if (cancelled) return;
      if (redir?.to) {
        window.location.replace(redir.to);
        return;
      }
      if (page) {
        setState({ status: "ok", page });
        return;
      }
      const entity = matchEntityRoute(pathname);
      if (entity) {
        setState({ status: "entity", entity, page: null });
      } else {
        setState({ status: "404", page: null });
      }
    });
    return () => { cancelled = true; };
  }, [pathname, locale]);

  if (state.status === "loading") {
    return <div className="min-h-screen bg-lin"><Header /><div className="max-w-4xl mx-auto py-24 px-6 text-ardoise/40">Chargement…</div><Footer /></div>;
  }
  if (state.status === "entity") {
    const fiche =
      state.entity.type === "building_room" ? <BuildingRoomPage pagePath={state.entity.pagePath} /> :
      state.entity.type === "accommodation" ? <AccommodationFichePage slug={state.entity.slug} /> :
      state.entity.type === "horse" ? <Navigate to={`/haras/chevaux/${state.entity.slug}`} replace /> :
      state.entity.type === "venue" ? <VenueFichePage slug={state.entity.slug} /> : null;
    if (fiche) {
      return <Suspense fallback={<div className="min-h-screen bg-lin"><Header /><Footer /></div>}>{fiche}</Suspense>;
    }
  }
  if (state.status === "404") return <NotFoundPage />;

  const p = state.page;
  const firstBlockIsHero = (p.blocks || [])[0]?.block_type === "hero";
  return (
    <div className="min-h-screen bg-lin flex flex-col overflow-x-clip" data-testid={`cms-page-${p.path.replace(/\//g, '-')}`}>
      <SEO title={p.seo_title || p.title} description={p.seo_description || undefined}
           exact={Boolean(p.seo_title && p.seo_title.includes("|"))}
           canonical={`https://domainedelabriouse.com${p.path === "/" ? "/" : p.path}`}
           ogImage={firstBlockIsHero && p.blocks[0]?.data?.image_url
             ? `https://domainedelabriouse.com${p.blocks[0].data.image_url}` : undefined} />
      {EVENT_VENUE_PATHS.includes(p.path) && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EventVenue",
            name: "Domaine de la Briouse",
            url: `https://domainedelabriouse.com${p.path}`,
            telephone: "+33749513090",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Domaine de la Briouse",
              addressLocality: "Sanxay",
              postalCode: "86600",
              addressRegion: "Nouvelle-Aquitaine",
              addressCountry: "FR",
            },
            maximumAttendeeCapacity: 200,
            description: "Jusqu'à 150 personnes assises et 200 personnes en configuration cocktail/debout (salle Rigoletto).",
            ...(p.blocks?.find((b) => b.block_type === "hero")?.data?.image_url
              ? { image: `https://domainedelabriouse.com${p.blocks.find((b) => b.block_type === "hero").data.image_url}` }
              : {}),
          })}</script>
        </Helmet>
      )}
      {LODGING_LD[p.path] && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": LODGING_LD[p.path].type,
            name: LODGING_LD[p.path].name,
            url: `https://domainedelabriouse.com${p.path}`,
            telephone: "+33749513090",
            numberOfRooms: LODGING_LD[p.path].rooms,
            address: {
              "@type": "PostalAddress",
              streetAddress: "Domaine de la Briouse",
              addressLocality: "Sanxay",
              postalCode: "86600",
              addressRegion: "Nouvelle-Aquitaine",
              addressCountry: "FR",
            },
            ...(p.blocks?.find((b) => b.block_type === "hero")?.data?.image_url
              ? { image: `https://domainedelabriouse.com${p.blocks.find((b) => b.block_type === "hero").data.image_url}` }
              : {}),
          })}</script>
        </Helmet>
      )}
      <Header />
      {p.path === WEDDING_PILLAR && <MariagePillarExtras />}
      <main className={`flex-1 mx-auto w-full ${firstBlockIsHero ? "max-w-5xl px-0" : "max-w-4xl py-16 px-6"}`}>
        {Object.keys(BREADCRUMB_SECTIONS).includes(p.path.split("/")[1]) && <SectionBreadcrumb page={p} hasHero={firstBlockIsHero} />}
        {!firstBlockIsHero && (
          <>
            <p className="eyebrow text-or">{p.template}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-domaine mt-4">{p.title}</h1>
            {p.seo_description && <p className="text-lg text-ardoise/70 mt-4 max-w-2xl">{p.seo_description}</p>}
          </>
        )}

        <div className={firstBlockIsHero ? "" : "mt-12 space-y-8"}>
          {(p.blocks || []).map((b, i) => (
            <div key={b.id || `block-${i}`}
                 className={firstBlockIsHero && i > 0 ? "max-w-5xl mx-auto px-6" : ""}>
              {/* SEO : un seul H1 par page — seul un Hero en 1er bloc rend un <h1> */}
              <BlockRenderer block={b} vars={vars} pageId={p.id} unit={unit}
                             isFirstHero={b.block_type === "hero" && i === 0} />
            </div>
          ))}
          {unit?.amenitiz_room_id && (
            <div id="reserver"
                 className={`max-w-xl ${firstBlockIsHero ? "mx-auto px-6 py-12" : "mt-12"}`}
                 data-testid="fiche-booking-section">
              <Suspense fallback={null}><AmenitizAvailability roomId={unit.amenitiz_room_id} /></Suspense>
            </div>
          )}
          {p.blocks?.length === 0 && (
            <div className="border border-dashed border-pierre bg-sable/20 p-12 text-center">
              <p className="text-sm text-ardoise/60 italic">Cette page n&apos;a pas encore de contenu.</p>
              <p className="text-xs text-ardoise/40 mt-2">L&apos;éditeur de blocs arrive en Phase 2 du CMS.</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
